/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useLocale } from '@/hooks/useLocale';
import { generateShelfSchema } from '../../utils/generateShelfSchema';
import type { SeoMetadataResponseModel } from '@/api/types/seo-data-types';
import type { ShelfPart } from '@/types/legacy';

export type ShelfSeoMetaDataProps = {
  seoMetaData: SeoMetadataResponseModel;
  parts: ShelfPart[];
  shelfCatLabel?: string;
};

const isEmptySeoMetadata = (metadata: SeoMetadataResponseModel): boolean => {
  return Object.values(metadata).every((value) => value === null || value === '' || value === true);
};

export const ShelfSeoMetaData = ({ seoMetaData, parts, shelfCatLabel }: ShelfSeoMetaDataProps) => {
  const locale = useLocale();
  const router = useRouter();

  const isMultiSelectURL = useMemo(() => {
    return /\/b\/.*~/.test(router.asPath);
  }, [router.asPath]);

  const schema = useMemo(() => {
    if (!parts[0]) {
      return '';
    }

    return JSON.stringify(
      generateShelfSchema({ parts, asPath: router.asPath, locale, shelfCatLabel })
    );
  }, [parts, router.asPath, locale, shelfCatLabel]);

  if (isEmptySeoMetadata(seoMetaData) && !parts[0]) {
    return null;
  }

  return (
    <>
      <Head>
        <title key="title">{seoMetaData.pageTitle}</title>

        {seoMetaData.pageTitle ? (
          <meta key="metaTitle" name="title" content={seoMetaData.pageTitle} />
        ) : null}
        {seoMetaData.indexEnabledFlag ? (
          <>
            {seoMetaData.pageContentDescription && (
              <meta
                key="metaDescription"
                name="description"
                content={seoMetaData.pageContentDescription}
              />
            )}
            {seoMetaData.pageTitle && (
              <meta key="metaTitle" name="og:title" content={seoMetaData.pageTitle} />
            )}
            {seoMetaData.currentPageUrl && (
              <meta key="metaUrl" property="og:url" content={seoMetaData.currentPageUrl} />
            )}
            {parts[0]?.productImageUrl && (
              <meta key="metaImage" property="og:image" content={parts[0]?.productImageUrl} />
            )}
            {seoMetaData.siteName && (
              <meta key="metaSiteName" property="og:site_name" content={seoMetaData.siteName} />
            )}
            <meta key="metaType" property="og:type" content="shelf" />
          </>
        ) : (
          <meta
            key="metaRobots"
            name="robots"
            content={isMultiSelectURL ? 'noindex, nofollow' : 'noindex, follow'}
          />
        )}

        {seoMetaData.robotsContent ? (
          <meta key="metaRobots" name="robots" content={seoMetaData.robotsContent} />
        ) : null}

        {seoMetaData.canonicalUrl && (
          <>
            <link key="linkCanonical" rel="canonical" href={`${seoMetaData.canonicalUrl}`} />
            <link
              key="linkAlternateLocale"
              rel="alternate"
              hrefLang={locale.toLowerCase()}
              href={seoMetaData.canonicalUrl}
            />
            {seoMetaData.alternateUrl && seoMetaData.langaugeLocale ? (
              <link
                key="linkAlternateObj"
                rel="alternate"
                hrefLang={seoMetaData.langaugeLocale}
                href={seoMetaData.alternateUrl}
              />
            ) : null}
          </>
        )}
        {schema ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: schema,
            }}
          />
        ) : null}
      </Head>
    </>
  );
};
